// Interview Integration Service - Handles interview scheduling and management
import AsyncStorage from '@react-native-async-storage/async-storage';

class InterviewIntegrationService {
  constructor() {
    this.isInitialized = false;
    this.interviews = new Map();
    this.schedules = new Map();
    this.notifications = [];
  }

  // Initialize the service
  async initialize() {
    try {
      console.log('🗓️ Initializing Interview Integration Service...');
      
      // Load cached data
      await this.loadCachedData();
      
      // Setup notification system
      await this.setupNotifications();
      
      this.isInitialized = true;
      console.log('✅ Interview Integration Service initialized successfully');
      
      return true;
    } catch (error) {
      console.error('❌ Failed to initialize Interview Integration Service:', error);
      return false;
    }
  }

  // Load cached data
  async loadCachedData() {
    try {
      const cachedInterviews = await AsyncStorage.getItem('cached_interviews');
      const cachedSchedules = await AsyncStorage.getItem('cached_schedules');
      
      if (cachedInterviews) {
        const interviews = JSON.parse(cachedInterviews);
        interviews.forEach(interview => {
          this.interviews.set(interview.interviewId, interview);
        });
      }
      
      if (cachedSchedules) {
        const schedules = JSON.parse(cachedSchedules);
        schedules.forEach(schedule => {
          this.schedules.set(schedule.scheduleId, schedule);
        });
      }
      
      console.log('📦 Cached interview data loaded');
    } catch (error) {
      console.error('❌ Error loading cached data:', error);
    }
  }

  // Setup notifications
  async setupNotifications() {
    // Setup interview reminders
    this.scheduleInterviewReminders();
  }

  // Schedule interview
  async scheduleInterview(interviewData) {
    try {
      const interview = {
        interviewId: interviewData.interviewId || `interview_${Date.now()}`,
        candidateId: interviewData.candidateId,
        jobId: interviewData.jobId,
        jobTitle: interviewData.jobTitle,
        company: interviewData.company,
        type: interviewData.type || 'video',
        scheduledAt: interviewData.scheduledAt,
        duration: interviewData.duration || 60,
        interviewer: interviewData.interviewer,
        location: interviewData.location || 'Video Call',
        status: 'scheduled',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      this.interviews.set(interview.interviewId, interview);
      await this.saveInterviewData();

      // Schedule reminder notifications
      await this.scheduleReminders(interview);

      console.log('📅 Interview scheduled successfully:', interview.interviewId);
      return interview;
    } catch (error) {
      console.error('❌ Error scheduling interview:', error);
      throw error;
    }
  }

  // Update interview status
  async updateInterviewStatus(interviewId, status, additionalData = {}) {
    try {
      const interview = this.interviews.get(interviewId);
      if (!interview) {
        throw new Error('Interview not found');
      }

      interview.status = status;
      interview.updatedAt = new Date().toISOString();
      
      // Add additional data
      Object.assign(interview, additionalData);

      this.interviews.set(interviewId, interview);
      await this.saveInterviewData();

      console.log(`📝 Interview status updated: ${interviewId} -> ${status}`);
      return interview;
    } catch (error) {
      console.error('❌ Error updating interview status:', error);
      throw error;
    }
  }

  // Get interview by ID
  getInterview(interviewId) {
    return this.interviews.get(interviewId);
  }

  // Get interviews by candidate
  getInterviewsByCandidate(candidateId) {
    return Array.from(this.interviews.values())
      .filter(interview => interview.candidateId === candidateId)
      .sort((a, b) => new Date(b.scheduledAt) - new Date(a.scheduledAt));
  }

  // Get upcoming interviews
  getUpcomingInterviews(candidateId = null) {
    const now = new Date();
    let interviews = Array.from(this.interviews.values())
      .filter(interview => new Date(interview.scheduledAt) > now);

    if (candidateId) {
      interviews = interviews.filter(interview => interview.candidateId === candidateId);
    }

    return interviews.sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt));
  }

  // Get past interviews
  getPastInterviews(candidateId = null) {
    const now = new Date();
    let interviews = Array.from(this.interviews.values())
      .filter(interview => new Date(interview.scheduledAt) <= now);

    if (candidateId) {
      interviews = interviews.filter(interview => interview.candidateId === candidateId);
    }

    return interviews.sort((a, b) => new Date(b.scheduledAt) - new Date(a.scheduledAt));
  }

  // Schedule reminders
  async scheduleReminders(interview) {
    try {
      const scheduledTime = new Date(interview.scheduledAt);
      const now = new Date();

      // 24 hour reminder
      const reminder24h = new Date(scheduledTime.getTime() - 24 * 60 * 60 * 1000);
      if (reminder24h > now) {
        this.notifications.push({
          id: `reminder_24h_${interview.interviewId}`,
          interviewId: interview.interviewId,
          type: '24h_reminder',
          scheduledFor: reminder24h.toISOString(),
          title: 'Interview Tomorrow',
          message: `You have an interview with ${interview.company} tomorrow at ${this.formatTime(scheduledTime)}`,
          data: interview
        });
      }

      // 1 hour reminder
      const reminder1h = new Date(scheduledTime.getTime() - 60 * 60 * 1000);
      if (reminder1h > now) {
        this.notifications.push({
          id: `reminder_1h_${interview.interviewId}`,
          interviewId: interview.interviewId,
          type: '1h_reminder',
          scheduledFor: reminder1h.toISOString(),
          title: 'Interview in 1 Hour',
          message: `Your interview with ${interview.company} starts in 1 hour`,
          data: interview
        });
      }

      // 15 minute reminder
      const reminder15m = new Date(scheduledTime.getTime() - 15 * 60 * 1000);
      if (reminder15m > now) {
        this.notifications.push({
          id: `reminder_15m_${interview.interviewId}`,
          interviewId: interview.interviewId,
          type: '15m_reminder',
          scheduledFor: reminder15m.toISOString(),
          title: 'Interview Starting Soon',
          message: `Your interview with ${interview.company} starts in 15 minutes`,
          data: interview
        });
      }

      console.log(`⏰ Reminders scheduled for interview: ${interview.interviewId}`);
    } catch (error) {
      console.error('❌ Error scheduling reminders:', error);
    }
  }

  // Schedule interview reminders (periodic check)
  scheduleInterviewReminders() {
    setInterval(() => {
      this.checkAndSendReminders();
    }, 60000); // Check every minute
  }

  // Check and send reminders
  async checkAndSendReminders() {
    try {
      const now = new Date();
      const dueReminders = this.notifications.filter(notification => {
        const scheduledTime = new Date(notification.scheduledFor);
        return scheduledTime <= now && !notification.sent;
      });

      for (const reminder of dueReminders) {
        await this.sendReminder(reminder);
        reminder.sent = true;
      }
    } catch (error) {
      console.error('❌ Error checking reminders:', error);
    }
  }

  // Send reminder
  async sendReminder(reminder) {
    try {
      // In a real app, this would trigger a push notification
      console.log(`🔔 Sending reminder: ${reminder.title} - ${reminder.message}`);
      
      // You could integrate with a notification service here
      // For now, we'll just log it
      
      return true;
    } catch (error) {
      console.error('❌ Error sending reminder:', error);
      return false;
    }
  }

  // Cancel interview
  async cancelInterview(interviewId, reason = '') {
    try {
      const interview = this.interviews.get(interviewId);
      if (!interview) {
        throw new Error('Interview not found');
      }

      interview.status = 'cancelled';
      interview.cancellationReason = reason;
      interview.cancelledAt = new Date().toISOString();
      interview.updatedAt = new Date().toISOString();

      this.interviews.set(interviewId, interview);
      await this.saveInterviewData();

      // Cancel related reminders
      this.notifications = this.notifications.filter(
        notification => notification.interviewId !== interviewId
      );

      console.log(`❌ Interview cancelled: ${interviewId}`);
      return interview;
    } catch (error) {
      console.error('❌ Error cancelling interview:', error);
      throw error;
    }
  }

  // Reschedule interview
  async rescheduleInterview(interviewId, newScheduledAt) {
    try {
      const interview = this.interviews.get(interviewId);
      if (!interview) {
        throw new Error('Interview not found');
      }

      const oldTime = interview.scheduledAt;
      interview.scheduledAt = newScheduledAt;
      interview.status = 'rescheduled';
      interview.previousScheduledAt = oldTime;
      interview.updatedAt = new Date().toISOString();

      this.interviews.set(interviewId, interview);
      await this.saveInterviewData();

      // Cancel old reminders and schedule new ones
      this.notifications = this.notifications.filter(
        notification => notification.interviewId !== interviewId
      );
      await this.scheduleReminders(interview);

      console.log(`📅 Interview rescheduled: ${interviewId}`);
      return interview;
    } catch (error) {
      console.error('❌ Error rescheduling interview:', error);
      throw error;
    }
  }

  // Get interview statistics
  getInterviewStatistics(candidateId) {
    const candidateInterviews = this.getInterviewsByCandidate(candidateId);
    
    const stats = {
      total: candidateInterviews.length,
      scheduled: candidateInterviews.filter(i => i.status === 'scheduled').length,
      completed: candidateInterviews.filter(i => i.status === 'completed').length,
      cancelled: candidateInterviews.filter(i => i.status === 'cancelled').length,
      upcoming: this.getUpcomingInterviews(candidateId).length,
      past: this.getPastInterviews(candidateId).length
    };

    return stats;
  }

  // Save interview data
  async saveInterviewData() {
    try {
      const interviewsArray = Array.from(this.interviews.values());
      await AsyncStorage.setItem('cached_interviews', JSON.stringify(interviewsArray));
    } catch (error) {
      console.error('❌ Error saving interview data:', error);
    }
  }

  // Format time for display
  formatTime(date) {
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  }

  // Format date for display
  formatDate(date) {
    return date.toLocaleDateString('en-GB', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  // Get next interview
  getNextInterview(candidateId) {
    const upcoming = this.getUpcomingInterviews(candidateId);
    return upcoming.length > 0 ? upcoming[0] : null;
  }

  // Check if candidate has interviews today
  hasInterviewsToday(candidateId) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const todayInterviews = this.getInterviewsByCandidate(candidateId)
      .filter(interview => {
        const interviewDate = new Date(interview.scheduledAt);
        return interviewDate >= today && interviewDate < tomorrow;
      });

    return todayInterviews.length > 0;
  }

  // Reset service
  reset() {
    this.interviews.clear();
    this.schedules.clear();
    this.notifications = [];
    this.isInitialized = false;
  }
}

export default InterviewIntegrationService;

